import { get_token } from "@/router";

const bolide_assist_query_url = process.env.VUE_APP_ASSIST_QUERY_URL;
const query_poll_interval_ms = 1500;
const query_poll_timeout_ms = 180000;
const query_id_pattern = /^[A-Za-z0-9_-]+$/;

function copy_present_property(target, source, property) {
  if (source[String(property)] != null && source[String(property)] !== "") {
    target[String(property)] = source[String(property)];
  }
}

export default {
  async query_assist(query_text, progress_context = {}) {
    const response = await fetch(bolide_assist_query_url, {
      method: "POST",
      headers: this.get_assist_request_headers(),
      body: JSON.stringify(
        this.build_assist_request_body(query_text, progress_context.conversation_id)
      ),
    });

    const data = await this.read_assist_response_json(response);
    this.assert_assist_response_ok(response, data);
    this.update_query_progress(progress_context, data);
    return this.is_assist_query_pending(data)
      ? this.poll_assist_query(this.get_assist_query_id(data), progress_context)
      : this.get_assist_response_text(data);
  },

  async poll_assist_query(query_id, progress_context = {}) {
    const detail_request = this.build_assist_query_detail_request(query_id);
    const started_at = Date.now();

    while (Date.now() - started_at < query_poll_timeout_ms) {
      const result = await this.poll_assist_query_once(detail_request, progress_context);
      if (result.complete) return this.get_assist_response_text(result.data);
    }
    throw new Error(
      "Siyanda Assist is still working on that request. Please check the chat history shortly."
    );
  },

  async poll_assist_query_once(detail_request, progress_context) {
    await this.wait(query_poll_interval_ms);
    const response = await this.send_assist_detail_request(detail_request);
    const data = await this.read_assist_response_json(response);
    this.assert_assist_response_ok(response, data);
    this.update_query_progress(progress_context, data);

    if (data.status === "FAILED") {
      throw new Error(this.get_assist_failure_message(data));
    }
    return ({
      complete: data.status === "COMPLETED",
      data,
    });
  },

  send_assist_detail_request(detail_request) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("GET", detail_request.url, true);
      detail_request.headers.forEach((value, name) => {
        request.setRequestHeader(name, value);
      });
      request.onload = () => resolve({
        ok: request.status >= 200 && request.status < 300,
        status: request.status,
        json: async () => JSON.parse(request.responseText),
      });
      request.onerror = () => reject(new Error("Siyanda Assist polling request failed."));
      request.send();
    });
  },

  build_assist_request_body(query_text, chat_id = null) {
    const body = {
      query_text,
      chat_id: chat_id || this.active_conversation_id || "default",
    };
    const scope = this.build_request_scope();
    if (scope) body.scope = scope;
    return body;
  },

  build_request_scope() {
    if (!this.scope?.level) return null;

    const scope = { level: this.scope.level };
    ["property_id", "property_name", "domain_hint"].forEach((property) => {
      copy_present_property(scope, this.scope, property);
    });
    if (this.scope.compare_property_ids?.length) {
      scope.compare_property_ids = this.scope.compare_property_ids;
    }
    return scope;
  },

  async fetch_assist_history(params = {}) {
    const response = await fetch(this.build_assist_history_url(params), {
      method: "GET",
      headers: this.get_assist_request_headers(),
    });
    const data = await this.read_assist_response_json(response);
    this.assert_assist_response_ok(response, data);
    return this.normalize_assist_page(data);
  },

  async fetch_assist_chat_summaries(params = {}) {
    const response = await fetch(this.build_assist_chat_history_url(params), {
      method: "GET",
      headers: this.get_assist_request_headers(),
    });
    const data = await this.read_assist_response_json(response);
    this.assert_assist_response_ok(response, data);
    return this.normalize_assist_page(data);
  },

  build_assist_history_url(params = {}) {
    return this.build_url_with_params(this.build_assist_history_base_url(), params);
  },

  build_assist_chat_history_url(params = {}) {
    return this.build_url_with_params(
      new URL("chats/", this.build_assist_history_base_url()).toString(),
      params
    );
  },

  build_assist_history_base_url() {
    if (!bolide_assist_query_url) {
      throw new Error("VUE_APP_ASSIST_QUERY_URL is not configured.");
    }

    const query_url = String(bolide_assist_query_url).replace(/\/+$/, "");
    return new URL(
      `${query_url.replace(/\/query$/, "/history")}/`,
      window.location.origin
    ).toString();
  },

  build_url_with_params(url_value, params = {}) {
    const history_url = new URL(url_value, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== "") history_url.searchParams.set(key, value);
    });
    return history_url.toString();
  },

  normalize_assist_page(data) {
    return ({
      ...data,
      count: Number(data.count || 0),
      next_cursor: data.next_cursor || null,
      results: Array.isArray(data.results) ? data.results : [],
    });
  },

  get_assist_request_headers() {
    if (!bolide_assist_query_url) {
      throw new Error("VUE_APP_ASSIST_QUERY_URL is not configured.");
    }
    const auth_token = get_token();
    if (!auth_token) throw new Error("No authentication token found.");
    return ({
      Authorization: `Token ${auth_token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    });
  },

  build_assist_query_detail_url(query_id) {
    const safe_query_id = String(query_id || "");
    if (!query_id_pattern.test(safe_query_id)) {
      throw new Error("Siyanda Assist returned an invalid query id.");
    }
    const encoded_query_id = encodeURIComponent(safe_query_id);
    return `${String(bolide_assist_query_url).replace(/\/+$/, "")}/${encoded_query_id}/`;
  },

  build_assist_query_detail_request(query_id) {
    const base_url = window.location.origin;
    const detail_url = new URL(this.build_assist_query_detail_url(query_id), base_url);
    const configured_url = new URL(bolide_assist_query_url, base_url);
    if (detail_url.origin !== configured_url.origin) {
      throw new Error("Siyanda Assist returned an invalid query URL.");
    }
    return new Request(detail_url.toString(), {
      method: "GET",
      headers: this.get_assist_request_headers(),
    });
  },

  async read_assist_response_json(response) {
    return response.json().catch(() => ({}));
  },

  assert_assist_response_ok(response, data) {
    if (response.ok) return;
    const error = new Error(this.get_assist_error_message(response, data));
    error.status = response.status;
    error.data = data;
    throw error;
  },

  get_assist_error_message(response, data) {
    return data.detail
      || data.query?.response_text
      || data.error
      || `Siyanda Assist request failed: ${response.status}`;
  },

  get_optional_assist_response_text(data) {
    return data.response_text || data.query?.response_text || "";
  },

  get_assist_response_text(data) {
    const response_text = this.get_optional_assist_response_text(data);
    if (response_text) return response_text;
    throw new Error("Siyanda Assist returned an empty response.");
  },

  get_assist_failure_message(data) {
    return this.get_optional_assist_response_text(data)
      || "Siyanda Assist could not process that request.";
  },

  is_assist_query_pending(data) {
    const query = data.query || data;
    return ["QUEUED", "PROCESSING"].includes(query?.status);
  },

  get_assist_query_id(data) {
    return data.id || data.query?.id || null;
  },

  update_query_progress(progress_context, data) {
    if (!progress_context.conversation_id || !progress_context.message_id) return;
    this.update_thinking_steps(
      progress_context.conversation_id,
      progress_context.message_id,
      data
    );
  },

  format_stage_label(stage) {
    const stage_labels = {
      QUERY_PROCESSOR: "Preparing request",
      FAST_GENERAL_RESPONSE: "Answering directly",
      CIRCUIT_BREAKER: "Safety check",
      GATEKEEPER: "Checking scope",
      INTENT: "Understanding intent",
      ARCHITECT: "Planning data retrieval",
      PROPERTY_ACCESS: "Checking property access",
      RETRIEVAL: "Fetching dashboard data",
      GENERATION: "Writing answer",
      VALIDATION: "Validating evidence",
      FINAL: "Finalizing",
    };

    if (stage_labels[String(stage || "")]) {
      return stage_labels[String(stage || "")];
    }

    return String(stage || "processing")
      .toLowerCase()
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  },

  format_query_status(status_value) {
    return this.format_stage_label(status_value || "processing");
  },

  format_stage_detail(log, query) {
    const payload = this.get_log_payload(log);

    if (this.is_failed_stage(log, query)) {
      return this.get_payload_error_message(payload) || "This stage could not complete.";
    }
    if (this.is_final_stage(log, query)) {
      return "Final answer is ready.";
    }

    if (log.stage === "QUERY_PROCESSOR") {
      return "Preparing the query and chat context.";
    }
    if (log.stage === "FAST_GENERAL_RESPONSE") {
      return "No dashboard retrieval is needed for this answer.";
    }
    if (log.stage === "CIRCUIT_BREAKER") {
      return "Checking whether the request can be handled safely.";
    }
    if (log.stage === "GATEKEEPER") {
      return "Confirming the request belongs in Siyanda Assist.";
    }
    if (log.stage === "INTENT") {
      return this.get_intent_stage_detail(payload);
    }
    if (log.stage === "ARCHITECT") {
      return this.get_architect_stage_detail(payload);
    }
    if (log.stage === "PROPERTY_ACCESS") {
      return this.get_property_access_stage_detail(payload);
    }
    if (log.stage === "RETRIEVAL") {
      return this.get_retrieval_stage_detail(payload);
    }
    if (log.stage === "GENERATION") {
      return "Turning retrieved data into a clear answer.";
    }
    if (log.stage === "VALIDATION") {
      return "Checking the answer against retrieved evidence.";
    }
    if (log.stage === "FINAL") {
      return "Preparing the final response.";
    }

    return log.status === "SUCCESS" ? "Stage completed." : "Stage is running.";
  },

  is_failed_stage(log, query) {
    return log.status === "FAILED" || query.status === "FAILED";
  },

  is_final_stage(log, query) {
    return query.status === "COMPLETED" && log.stage === "FINAL";
  },

  get_log_payload(log) {
    return log?.payload && typeof log.payload === "object" ? log.payload : {};
  },

  get_payload_error_message(payload) {
    return payload.error_message || payload.error || payload.detail || "";
  },

  get_intent_stage_detail(payload) {
    const intent = payload.intent_code || payload.intent || payload.intent_name;
    const confidence = Number(payload.confidence_score ?? payload.confidence);

    if (intent && Number.isFinite(confidence)) {
      const confidence_percent = confidence > 1 ? confidence : confidence * 100;
      return `${this.format_stage_label(intent)} matched with ${Math.round(confidence_percent)}% confidence.`;
    }

    if (intent) {
      return `${this.format_stage_label(intent)} matched for this request.`;
    }

    return "Detecting request type, entities, and scope.";
  },

  get_architect_stage_detail(payload) {
    const selected_tool = payload.selected_tool || payload.tool || payload.capability_tool;
    const decision = payload.architect_decision || payload.decision;

    if (decision && String(decision).includes("STOP")) {
      return "Preparing a direct answer without dashboard retrieval.";
    }

    if (selected_tool) {
      return `Selected ${this.format_tool_name(selected_tool)} for verified data retrieval.`;
    }

    return "Choosing the safest data path for the request.";
  },

  get_property_access_stage_detail(payload) {
    const compare_ids = Array.isArray(payload.compare_property_ids)
      ? payload.compare_property_ids
      : [];
    const property_name = payload.property_name
      || payload.current_property_name
      || payload.architect_property_name;

    if (compare_ids.length) {
      return `Checking access for ${compare_ids.length} selected properties.`;
    }

    if (property_name) {
      return `Checking access to ${property_name}.`;
    }

    return "Checking property permissions for this request.";
  },

  get_retrieval_stage_detail(payload) {
    const row_count = payload.row_count ?? payload.rows_count ?? payload.count;
    const selected_tool = payload.selected_tool || payload.tool;
    const comparison_count = payload.comparison_property_count || payload.property_count;

    if (comparison_count) {
      return `Retrieving comparison data for ${comparison_count} properties.`;
    }

    if (row_count != null) {
      return `Retrieved ${row_count} data ${Number(row_count) === 1 ? "row" : "rows"}.`;
    }

    if (selected_tool) {
      return `Running ${this.format_tool_name(selected_tool)} against verified sources.`;
    }

    return "Retrieving verified dashboard data.";
  },

  format_tool_name(tool_name) {
    return String(tool_name || "")
      .replace(/^get_/, "")
      .replace(/_/g, " ")
      .trim()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  },
};
